import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Tag, Button, Space, message, Typography, Descriptions,
  Input, Modal, Form, Select, InputNumber, Popconfirm, Divider, Alert,
} from 'antd';
import {
  ApiOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LinkOutlined,
  VideoCameraOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import { adminAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;

// 默认模型列表（对齐 Qiwise 上游实际提供的两个 Seedance 视频模型）
// metadata 结构必须与前端 CreativeInspiration 一致：resolutions 为 {value,label} 对象数组、含 aspectRatios，否则前端崩溃
const DEFAULT_MODELS = [
  {
    model_name: 'qiwise-seedance-2-0', display_name: 'Qiwise Seedance 2.0 Pro', model_type: 'video',
    upstream_model_name: 'doubao-seedance-2-0-260128', input_price: 6.24, output_price: 0,
    billing_type: 'per_token', billing_rate: 0, api_path: '/api/v3/contents/generations/tasks', status: 'active', priority: 10,
    metadata: JSON.stringify({
      version: '2.0', tier: '旗舰版', tierColor: '#ff4d4f', desc: 'Qiwise Seedance 2.0 Pro',
      features: ['1080p Ultra HD', '15s Duration', 'Multimodal Reference', 'Audio Generation'],
      resolutions: [{ value: '480p', label: '480p 标清', disabled: false }, { value: '720p', label: '720p 高清', disabled: false }, { value: '1080p', label: '1080p 超清', disabled: false }],
      durationRange: [4, 15], fpsOptions: [24], aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9', 'adaptive'],
      maxRefImages: 10, maxRefVideos: 3, maxRefAudio: 3,
      resolution_pricing: { '480p': 2.80, '720p': 6.24, '1080p': 7.70 },
      resolution_pricing_with_video: { '480p': 1.20, '720p': 3.80, '1080p': 4.70 },
      min_discount_rate: 1.00,
    }),
  },
  {
    model_name: 'qiwise-seedance-2-0-fast', display_name: 'Qiwise Seedance 2.0 Fast', model_type: 'video',
    upstream_model_name: 'doubao-seedance-2-0-fast-260128', input_price: 5.02, output_price: 0,
    billing_type: 'per_token', billing_rate: 0, api_path: '/api/v3/contents/generations/tasks', status: 'active', priority: 9,
    metadata: JSON.stringify({
      version: '2.0 Fast', tier: '快速版', tierColor: '#52c41a', desc: 'Qiwise Seedance 2.0 Fast',
      features: ['720p HD', '15s Duration', 'Fast Output', 'Multimodal Reference'],
      resolutions: [{ value: '480p', label: '480p 标清', disabled: false }, { value: '720p', label: '720p 高清', disabled: false }],
      durationRange: [4, 15], fpsOptions: [24], aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', 'adaptive'],
      maxRefImages: 10, maxRefVideos: 3, maxRefAudio: 3,
      resolution_pricing: { '480p': 2.10, '720p': 5.02 },
      resolution_pricing_with_video: { '480p': 1.00, '720p': 3.03 },
      min_discount_rate: 1.00,
    }),
  },
];

// 从模型的 metadata 字段解析特性信息
const parseModelMetadata = (model) => {
  if (!model?.metadata) return null;
  try {
    const meta = typeof model.metadata === 'string' ? JSON.parse(model.metadata) : model.metadata;
    return {
      version: meta.version || '', tier: meta.tier || '', tierColor: meta.tierColor || 'default',
      desc: meta.desc || '', features: meta.features || [], minDiscountRate: meta.min_discount_rate, fixedDiscountRate: meta.discount_rate,
    };
  } catch {
    return { tier: '', tierColor: 'default', desc: '', features: [] };
  }
};

const getFeatureTags = (model) => {
  const meta = parseModelMetadata(model);
  return meta?.features || [];
};

const Qiwise = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // 供应商
  const [qwProvider, setQwProvider] = useState(null);

  // 模型
  const [allModels, setAllModels] = useState([]);
  const [seedanceModels, setSeedanceModels] = useState([]);

  // Provider 编辑表单
  const [providerForm] = Form.useForm();
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [providerEditMode, setProviderEditMode] = useState(false);

  // Model 编辑表单
  const [modelForm] = Form.useForm();
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [modelEditMode, setModelEditMode] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  // 折扣策略：follow_key（跟随APIKey折扣）/ min（折扣下限）/ fixed（固定折扣，覆盖APIKey）
  const [discountMode, setDiscountMode] = useState('min'); // 默认：折扣下限（配合 1.00 即禁折，保持 Qiwise 现状）
  const [discountValue, setDiscountValue] = useState(1.0); // min 或 fixed 模式下的折扣数值（0.01~1.00）

  // ========== 数据加载 ==========
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [provRes, modelRes] = await Promise.all([
        adminAPI.getProviders(),
        adminAPI.getModelConfigs(),
      ]);

      let providers = [];
      let models = [];
      let qiwise = null;

      if (provRes.code === 0) {
        providers = provRes.data?.list || provRes.data || [];
        qiwise = providers.find(p =>
          p.name?.toLowerCase().includes('qiwise') ||
          p.api_base?.toLowerCase().includes('qiwise')
        );
        setQwProvider(qiwise || null);
      }

      if (modelRes.code === 0) {
        models = modelRes.data?.list || modelRes.data || [];
        setAllModels(models);
      }

      // 过滤 Qiwise provider 下的模型
      const currentQWProvider = qwProvider || qiwise;
      const qwModels = models.filter(m =>
        currentQWProvider ? m.provider_id === currentQWProvider.id : false
      );
      // 回退：按名字含 qiwise 过滤
      const qwModelsFallback = models.filter(m => m.model_name?.toLowerCase().includes('qiwise'));
      const finalQWModels = qwModels.length > 0 ? qwModels : qwModelsFallback;

      setSeedanceModels(finalQWModels.filter(m => m.type === 'video' || m.model_name?.toLowerCase().includes('seedance')));
    } catch (e) {
      console.error('Failed to fetch Qiwise data:', e);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ========== 供应商 CRUD ==========
  const openCreateProvider = () => {
    setProviderEditMode(false);
    providerForm.resetFields();
    providerForm.setFieldsValue({
      name: 'qiwise-seedance',
      display_name: 'Qiwise Seedance',
      api_base: 'https://seedance.qiwise.cn',
      status: 'active',
      adapter_type: 'openai',
      provider_type: 'internal',
    });
    setProviderModalOpen(true);
  };

  const openEditProvider = () => {
    if (!qwProvider) return;
    setProviderEditMode(true);
    setProviderModalOpen(true);
  };

  useEffect(() => {
    if (providerModalOpen && providerEditMode && qwProvider) {
      providerForm.setFieldsValue(qwProvider);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerModalOpen]);

  const handleProviderSave = async () => {
    try {
      const values = await providerForm.validateFields();
      setSaving(true);
      let res;
      if (providerEditMode && qwProvider) {
        res = await adminAPI.updateProvider(qwProvider.id, { ...qwProvider, ...values });
      } else {
        res = await adminAPI.createProvider(values);
      }
      if (res.code === 0) {
        message.success(providerEditMode ? '供应商更新成功' : '供应商创建成功');
        setProviderModalOpen(false);
        fetchData();
      } else {
        message.error(res.message || '操作失败');
      }
    } catch (e) {
      if (e.errorFields) return;
      message.error('操作失败: ' + (e.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProvider = async () => {
    if (!qwProvider) return;
    try {
      const res = await adminAPI.deleteProvider(qwProvider.id);
      if (res.code === 0) {
        message.success('供应商已删除');
        setQwProvider(null);
        fetchData();
      } else {
        message.error(res.message || '删除失败');
      }
    } catch (e) {
      message.error('删除失败: ' + (e.message || ''));
    }
  };

  // ========== 模型 CRUD ==========
  const openCreateModel = () => {
    setModelEditMode(false);
    setEditingModel(null);
    setDiscountMode('min');
    setDiscountValue(1.0);
    setModelModalOpen(true);
  };

  const populateModelForm = (m) => {
    let metaStr = '';
    let fixedRate, minRate;
    if (m.metadata) {
      try {
        const obj = typeof m.metadata === 'string' ? JSON.parse(m.metadata) : m.metadata;
        fixedRate = obj.discount_rate;
        minRate = obj.min_discount_rate;
        // 折扣字段由「折扣策略」选择器管理，不在 JSON 文本框展示，避免与选择器重复/混淆。
        // 保存时 applyDiscountToMetadata 会按选择器重新写回，因此这里剥离不会丢数据。
        const display = { ...obj };
        delete display.discount_rate;
        delete display.min_discount_rate;
        metaStr = JSON.stringify(display, null, 2);
      } catch (e) { metaStr = typeof m.metadata === 'string' ? m.metadata : ''; }
    }
    // 优先级与网关一致：discount_rate（固定）> min_discount_rate（下限）> 跟随 Key
    if (typeof fixedRate === 'number' && fixedRate > 0 && fixedRate <= 1.0) {
      setDiscountMode('fixed'); setDiscountValue(fixedRate);
    } else if (typeof minRate === 'number' && minRate > 0 && minRate <= 1.0) {
      setDiscountMode('min'); setDiscountValue(minRate);
    } else {
      setDiscountMode('follow_key'); setDiscountValue(1.0);
    }
    modelForm.setFieldsValue({
      provider_id: m.provider_id || qwProvider?.id || 0,
      model_name: m.model_name, display_name: m.display_name,
      input_price: m.input_price, output_price: m.output_price || 0,
      api_path: m.api_path || '/api/v3/contents/generations/tasks', status: m.status, priority: m.priority || 5,
      model_type: 'video',
      upstream_model_name: m.upstream_model_name || '',
      billing_type: m.billing_type || 'per_token', billing_rate: m.billing_rate || 0,
      metadata: metaStr,
    });
  };

  const openEditModel = (model) => {
    setModelEditMode(true);
    setEditingModel(model);
    setModelModalOpen(true);
  };

  useEffect(() => {
    if (!modelModalOpen) return;
    if (editingModel) {
      populateModelForm(editingModel);
    } else {
      modelForm.resetFields();
      setDiscountMode('min');
      setDiscountValue(1.0);
      modelForm.setFieldsValue({
        model_type: 'video',
        provider_id: qwProvider?.id || 0,
        status: 'active',
        input_price: 6.24,
        output_price: 0,
        billing_type: 'per_token',
        billing_rate: 0,
        api_path: '/api/v3/contents/generations/tasks',
        priority: 5,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelModalOpen]);

  // 按折扣策略合并到 metadata。三种模式互斥，写入前清掉另外两种的字段，避免残留导致优先级混乱。
  // 网关优先级：discount_rate（固定，覆盖Key） > min_discount_rate（下限） > 跟随Key折扣
  const applyDiscountToMetadata = (metaStr) => {
    let obj = {};
    if (metaStr) {
      try { obj = JSON.parse(metaStr); } catch (e) { throw new Error('metadata JSON 格式错误'); }
    }
    // 先清空两个折扣字段，再按当前模式写入，保证互斥
    delete obj.discount_rate;
    delete obj.min_discount_rate;
    if (discountMode === 'fixed') {
      if (!(discountValue > 0 && discountValue <= 1.0)) throw new Error('固定折扣需在 0.01~1.00 之间');
      obj.discount_rate = discountValue;
    } else if (discountMode === 'min') {
      if (!(discountValue > 0 && discountValue <= 1.0)) throw new Error('折扣下限需在 0.01~1.00 之间');
      obj.min_discount_rate = discountValue;
    }
    // follow_key：两个字段都不写，跟随 API Key 折扣
    return JSON.stringify(obj);
  };

  const handleModelSave = async () => {
    try {
      const values = await modelForm.validateFields();
      setSaving(true);

      let mergedMetadata;
      try {
        mergedMetadata = applyDiscountToMetadata(values.metadata);
      } catch (e) {
        message.error(e.message);
        setSaving(false);
        return;
      }

      const data = {
        ...values,
        metadata: mergedMetadata,
        provider_id: qwProvider?.id || 0,
        provider_name: qwProvider?.name || 'qiwise-seedance',
      };

      let res;
      if (modelEditMode && editingModel) {
        res = await adminAPI.updateModelConfig(editingModel.id, data);
      } else {
        res = await adminAPI.createModelConfig(data);
      }
      if (res.code === 0) {
        message.success(modelEditMode ? '模型更新成功' : '模型创建成功');
        setModelModalOpen(false);
        fetchData();
      } else {
        message.error(res.message || '操作失败');
      }
    } catch (e) {
      if (e.errorFields) return;
      message.error('操作失败: ' + (e.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModel = async (model) => {
    try {
      const res = await adminAPI.deleteModelConfig(model.id);
      if (res.code === 0) {
        message.success('删除成功');
        fetchData();
      } else {
        message.error(res.message || '删除失败');
      }
    } catch (e) {
      message.error('删除失败: ' + (e.message || ''));
    }
  };

  // 一键创建默认模型
  const handleCreateDefaults = async () => {
    if (!qwProvider) {
      message.warning('请先创建 Qiwise 供应商');
      return;
    }
    setSaving(true);
    let success = 0;
    let skipped = 0;
    for (const model of DEFAULT_MODELS) {
      const exists = allModels.find(m => m.model_name === model.model_name);
      if (exists) { skipped++; continue; }
      try {
        const res = await adminAPI.createModelConfig({
          ...model,
          provider_id: qwProvider.id,
          provider_name: qwProvider.name,
        });
        if (res.code === 0) success++;
      } catch (e) { /* skip */ }
    }
    setSaving(false);
    message.success(`创建成功 ${success} 个模型` + (skipped > 0 ? `，跳过 ${skipped} 个已存在的` : ''));
    fetchData();
  };

  // ========== 表格列 ==========
  const modelColumns = [
    {
      title: '模型名称', dataIndex: 'model_name', key: 'model_name', width: 200,
      render: (name, record) => {
        const meta = parseModelMetadata(record);
        return (
          <Space direction="vertical" size={2}>
            <Space size={4}>
              <Text strong>{record.display_name || name}</Text>
              {meta?.tier && <Tag color={meta.tierColor} style={{ fontSize: 11 }}>{meta.tier}</Tag>}
            </Space>
            <Text type="secondary" style={{ fontSize: 11 }}>{name}</Text>
          </Space>
        );
      },
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 80,
      render: (status) => <Tag color={status === 'active' ? 'green' : 'red'}>{status === 'active' ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '单价', key: 'unit_price', width: 120,
      render: (_, r) => <span style={{ fontWeight: 600, color: '#ff4d4f' }}>${parseFloat(r.input_price || 0).toFixed(2)}<small style={{ color: '#888', fontWeight: 400 }}>/M tokens</small></span>,
    },
    {
      title: '折扣策略', key: 'discount', width: 130,
      render: (_, r) => {
        const meta = parseModelMetadata(r);
        const fixed = meta?.fixedDiscountRate;
        const min = meta?.minDiscountRate;
        if (typeof fixed === 'number' && fixed > 0 && fixed <= 1.0) {
          return <Tag color="purple">固定 {fixed}</Tag>;
        }
        if (typeof min === 'number' && min >= 1.0) {
          return <Tag color="volcano">禁止折扣</Tag>;
        }
        if (typeof min === 'number' && min > 0 && min < 1.0) {
          return <Tag color="orange">下限 {min}</Tag>;
        }
        return <Tag color="green">跟随 Key</Tag>;
      },
    },
    { title: '上游模型', dataIndex: 'upstream_model_name', key: 'upstream', width: 220, ellipsis: true, render: (v) => <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{v || '-'}</Text> },
    {
      title: '特性', dataIndex: 'metadata', key: 'features', width: 220,
      render: (_, record) => {
        const tags = getFeatureTags(record);
        if (tags.length === 0) return <Text type="secondary">未配置</Text>;
        return (
          <Space size={2} wrap>
            {tags.slice(0, 3).map((f, i) => (
              <Tag key={i} style={{ fontSize: 11, margin: '2px 0' }}>{f}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '操作', key: 'actions', width: 110, fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditModel(record)} />
          <Popconfirm title="确定删除此模型？" onConfirm={() => handleDeleteModel(record)} okText="删除" cancelText="取消">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ========== 渲染 ==========
  return (
    <div style={{ padding: '0 24px 24px', maxWidth: 1400 }}>
      {/* 页头 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Space align="center" size={12}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ApiOutlined style={{ fontSize: 20, color: '#fff' }} />
            </div>
            <div>
              <Title level={3} style={{ margin: 0 }}>Qiwise 渠道配置</Title>
              <Space size={4}>
                {qwProvider ? <Tag color="green" icon={<CheckCircleOutlined />}>已接入</Tag> : <Tag color="orange" icon={<CloseCircleOutlined />}>未接入</Tag>}
                {seedanceModels.length > 0 && <Tag color="blue">{seedanceModels.length} 个模型</Tag>}
              </Space>
            </div>
          </Space>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchData}>刷新</Button>
        </Space>
      </div>

      {/* 说明 */}
      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        message="Qiwise 渠道接入说明"
        description="Qiwise 提供火山引擎原生格式的 Doubao Seedance 视频生成模型（仅视频，无图片模型）。直接传图 URL，不需要 AIGC 素材库。可为每个模型单独设置折扣策略（固定折扣 / 折扣下限 / 跟随 Key）。本页面一站式管理 Qiwise 渠道的供应商和模型配置。"
        style={{ marginBottom: 24 }}
      />

      {/* ======== 供应商配置 ======== */}
      <Card
        title={<Space><KeyOutlined style={{ color: '#11998e' }} /><span>供应商配置</span></Space>}
        extra={
          <Space>
            {qwProvider ? (
              <>
                <Button size="small" icon={<EditOutlined />} onClick={openEditProvider}>编辑</Button>
                <Popconfirm title="确定删除此供应商？关联的模型将不可用。" onConfirm={handleDeleteProvider} okText="删除" cancelText="取消">
                  <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
                </Popconfirm>
              </>
            ) : (
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openCreateProvider}>创建供应商</Button>
            )}
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        {qwProvider ? (
          <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} bordered size="small">
            <Descriptions.Item label="供应商名称"><Text code>{qwProvider.name}</Text></Descriptions.Item>
            <Descriptions.Item label="显示名称">{qwProvider.display_name}</Descriptions.Item>
            <Descriptions.Item label="状态"><Tag color={qwProvider.status === 'active' ? 'green' : 'red'}>{qwProvider.status === 'active' ? '启用' : '禁用'}</Tag></Descriptions.Item>
            <Descriptions.Item label="API 地址" span={2}>
              <Space><LinkOutlined /> <Text code>{qwProvider.api_base}</Text></Space>
            </Descriptions.Item>
            <Descriptions.Item label="适配器">{qwProvider.adapter_type || 'openai'}</Descriptions.Item>
            <Descriptions.Item label="API Key" span={3}>
              <Text code>{qwProvider.api_key ? qwProvider.api_key.substring(0, 10) + '****' + qwProvider.api_key.slice(-4) : '未配置'}</Text>
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <KeyOutlined style={{ fontSize: 40, color: '#d9d9d9', marginBottom: 16 }} />
            <Paragraph type="secondary">尚未配置 Qiwise 供应商。点击上方「创建供应商」按钮开始配置。</Paragraph>
            <Paragraph type="secondary" style={{ fontSize: 12 }}>
              API Base URL: <Text code>https://seedance.qiwise.cn</Text>
            </Paragraph>
          </div>
        )}
      </Card>

      {/* ======== Seedance 视频模型 ======== */}
      <Card
        title={
          <Space>
            <VideoCameraOutlined style={{ color: '#11998e' }} />
            <span>Seedance 视频模型</span>
            <Tag color="blue">{seedanceModels.length}</Tag>
          </Space>
        }
        extra={
          <Space>
            {!qwProvider && <Text type="secondary" style={{ fontSize: 12 }}>请先创建供应商</Text>}
            <Button size="small" disabled={!qwProvider} onClick={handleCreateDefaults} loading={saving}>一键创建默认模型</Button>
            <Button type="primary" size="small" icon={<PlusOutlined />} disabled={!qwProvider} onClick={openCreateModel}>添加视频模型</Button>
          </Space>
        }
      >
        <Table
          columns={modelColumns}
          dataSource={seedanceModels}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={false}
          scroll={{ x: 1080 }}
          locale={{ emptyText: '暂无 Seedance 视频模型' }}
        />
      </Card>

      {/* ======== Provider 编辑弹窗 ======== */}
      <Modal
        title={providerEditMode ? '编辑 Qiwise 供应商' : '创建 Qiwise 供应商'}
        open={providerModalOpen}
        onCancel={() => setProviderModalOpen(false)}
        onOk={handleProviderSave}
        confirmLoading={saving}
        width={560}
        destroyOnHidden
      >
        <Form form={providerForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="供应商名称" rules={[{ required: true, message: '请输入供应商名称' }]}>
            <Input placeholder="qiwise-seedance" />
          </Form.Item>
          <Form.Item name="display_name" label="显示名称" rules={[{ required: true, message: '请输入显示名称' }]}>
            <Input placeholder="Qiwise Seedance" />
          </Form.Item>
          <Form.Item name="api_base" label="API Base URL" rules={[{ required: true, message: '请输入 API 地址' }]}>
            <Input placeholder="https://seedance.qiwise.cn" />
          </Form.Item>
          <Form.Item name="api_key" label="API Key" rules={[{ required: true, message: '请输入 API Key' }]}>
            <Input.Password placeholder="sk-..." />
          </Form.Item>
          <Form.Item name="adapter_type" label="适配器类型">
            <Select options={[{ value: 'openai', label: 'OpenAI 兼容' }, { value: 'custom', label: '自定义' }]} />
          </Form.Item>
          <Form.Item name="provider_type" label="供应商类型">
            <Select options={[
              { value: 'internal', label: '内部供应商（默认）' },
              { value: 'external', label: '外部供应商（模型ID: 供应商名/模型名）' },
            ]} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select options={[{ value: 'active', label: '启用' }, { value: 'inactive', label: '禁用' }]} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ======== Model 编辑弹窗 ======== */}
      <Modal
        title={modelEditMode ? '编辑模型配置' : '添加视频模型'}
        open={modelModalOpen}
        onCancel={() => setModelModalOpen(false)}
        onOk={handleModelSave}
        confirmLoading={saving}
        width={640}
      >
        <Form form={modelForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="model_name" label="模型名称 (model)" rules={[{ required: true, message: '请输入模型名称' }]}>
            <Input placeholder="qiwise-seedance-2-0" />
          </Form.Item>
          <Form.Item name="display_name" label="显示名称" rules={[{ required: true, message: '请输入显示名称' }]}>
            <Input placeholder="Qiwise Seedance 2.0 Pro" />
          </Form.Item>
          <Form.Item name="model_type" label="模型类型" hidden><Input /></Form.Item>
          <Form.Item name="provider_id" label="供应商ID" hidden><Input /></Form.Item>
          <Form.Item name="billing_type" label="计费方式" hidden><Input /></Form.Item>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="input_price" label="单价（$/百万tokens）" rules={[{ required: true }]}>
              <InputNumber min={0} step={0.01} style={{ width: 200 }} placeholder="6.24" />
            </Form.Item>
            <Form.Item name="priority" label="优先级">
              <InputNumber min={0} max={100} style={{ width: 100 }} placeholder="5" />
            </Form.Item>
          </Space>
          <Form.Item
            label="折扣策略"
            tooltip={{
              overlayStyle: { maxWidth: 460 },
              title: (
                <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                  <div>折扣率：1.00=原价，0.90=9折，0.50=5折（越小越优惠）。</div>
                  <div style={{ marginTop: 4 }}><b>三种策略：</b></div>
                  <div>• <b>跟随 API Key</b>：客户 Key 几折就几折，正常参与你的折扣体系。</div>
                  <div>• <b>折扣下限</b>：客户折扣不能超过此下限。填 1.00 = 谁都不许打折（高成本模型防亏本）。</div>
                  <div>• <b>固定折扣</b>：无视客户 Key，全员强制此折扣（想给某模型单独定价/促销时用）。</div>
                  <div style={{ marginTop: 4 }}><b>优先级：</b>固定折扣 &gt; 折扣下限 &gt; 跟随 Key（三者互斥，选一个即可）。</div>
                  <div style={{ marginTop: 4 }}><b>举例</b>（客户 Key 8.5 折）：下限=1.00→原价；下限=0.90→9折；固定=0.80→8折。</div>
                  <div style={{ marginTop: 4 }}>折扣只需在此设置，无需写进下方 JSON，保存自动写入。</div>
                </div>
              ),
            }}
          >
            <Space align="start" wrap>
              <Select
                value={discountMode}
                onChange={setDiscountMode}
                style={{ width: 220 }}
                options={[
                  { value: 'follow_key', label: '跟随 API Key 折扣' },
                  { value: 'min', label: '折扣下限（不能更优惠）' },
                  { value: 'fixed', label: '固定折扣（覆盖 Key 折扣）' },
                ]}
              />
              {discountMode !== 'follow_key' && (
                <InputNumber
                  value={discountValue}
                  onChange={(v) => setDiscountValue(v ?? 1.0)}
                  min={0.01} max={1.0} step={0.01} style={{ width: 130 }}
                  addonBefore={discountMode === 'fixed' ? '固定' : '下限'}
                  placeholder="1.00"
                />
              )}
            </Space>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
              {discountMode === 'follow_key' && '当前：用户 API Key 自身的折扣生效（不做任何限制）'}
              {discountMode === 'min' && `当前：折扣下限=${discountValue} —— Key 折扣不能比它更优惠；填 1.00 即完全禁折（按原价）`}
              {discountMode === 'fixed' && `当前：固定折扣=${discountValue} —— 无视用户 Key 折扣，一律按此折扣计费`}
            </Text>
          </Form.Item>
          <Form.Item name="upstream_model_name" label="上游模型名" extra="Qiwise 火山原生模型 ID" rules={[{ required: true, message: '请输入上游模型名' }]}>
            <Input placeholder="doubao-seedance-2-0-260128" />
          </Form.Item>
          <Form.Item name="api_path" label="API 路径" extra="Qiwise 火山原生创建端点">
            <Input placeholder="/api/v3/contents/generations/tasks" />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select options={[{ value: 'active', label: '启用' }, { value: 'inactive', label: '禁用' }]} />
          </Form.Item>
          <Form.Item name="output_price" hidden><InputNumber /></Form.Item>
          <Form.Item name="billing_rate" hidden><InputNumber /></Form.Item>

          {/* ========== 特性/元数据编辑 ========== */}
          <Divider plain style={{ fontSize: 13, color: '#888' }}>模型特性（Metadata）</Divider>
          <Form.Item name="metadata" label="元数据 JSON" extra="编辑模型的特性、等级、描述、分辨率、比例、定价等。分辨率须为 {value,label} 对象数组，且含 aspectRatios，否则前端灵感创作页会报错。">
            <Input.TextArea
              rows={modelEditMode ? 14 : 10}
              placeholder={`{\n  "version": "2.0",\n  "tier": "旗舰版",\n  "desc": "Qiwise Seedance 2.0 Pro",\n  "features": ["1080p Ultra HD", "15s Duration"],\n  "resolutions": [{"value":"480p","label":"480p 标清"},{"value":"720p","label":"720p 高清"}],\n  "durationRange": [4, 15],\n  "fpsOptions": [24],\n  "aspectRatios": ["16:9","9:16","1:1"]\n}`}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </Form.Item>
          <Button size="small" type="link" style={{ marginTop: -16, marginBottom: 12, padding: 0 }} onClick={() => {
            const val = modelForm.getFieldValue('metadata');
            if (!val) return;
            try { modelForm.setFieldsValue({ metadata: JSON.stringify(JSON.parse(val), null, 2) }); message.success('JSON 已格式化'); } catch (e) { message.error('JSON 格式错误'); }
          }}>格式化 JSON</Button>

          <Alert
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            message="Metadata 字段说明"
            description={
              <div style={{ fontSize: 11, maxHeight: 130, overflow: 'auto' }}>
                <div><Text code>resolutions</Text>: 分辨率，须为 {`{value,label}`} 对象数组（如 {`{"value":"480p","label":"480p 标清"}`}）</div>
                <div><Text code>aspectRatios</Text>: 画面比例字符串数组（如 ["16:9","9:16"]）— 缺失会导致前端崩溃</div>
                <div><Text code>durationRange</Text> / <Text code>fpsOptions</Text>: 时长范围、帧率（Qiwise 固定 24）</div>
                <div><Text code>features</Text> / <Text code>tier</Text> / <Text code>tierColor</Text> / <Text code>desc</Text>: 展示信息</div>
                <div><Text code>resolution_pricing</Text> / <Text code>resolution_pricing_with_video</Text>: 分档定价</div>
                <div><Text code>discount_rate</Text> / <Text code>min_discount_rate</Text>: 由上方「折扣策略」管理，不在此 JSON 显示，无需手写</div>
              </div>
            }
            style={{ marginTop: -8 }}
          />
        </Form>
      </Modal>
    </div>
  );
};

export default Qiwise;
